import Image from 'next/image';
import { useTranslations } from 'next-intl';
import styled from 'styled-components';
import { Button, Reveal, Section, sectionTitleStyle } from '@/components/ui';
import config from '@/config';
import { up } from '@/themes';

const DOWNLOAD_URL = `https://www.bidali.com/app?utm_campaign=${config.appDownloadCampaign}&utm_medium=giftcards_site&utm_source=giftcard_site&utm_content=app_download_cta`;

/* Brand-gradient panel, as CtaPanel, with the app screenshot rising from its bottom edge. */
const Panel = styled(Reveal)`
  background-image: var(--gradient-brand);
  border-radius: var(--radius-3xl);
  box-shadow: var(--shadow-lg);
  display: grid;
  gap: 40px;
  isolation: isolate;
  overflow: hidden;
  padding: 48px 24px 0;
  position: relative;
  text-align: center;

  ${up.lg} {
    align-items: center;
    gap: 48px;
    grid-template-columns: minmax(0, 1fr) minmax(0, 320px);
    padding: 0 64px;
    text-align: left;
  }

  /* Soft highlight from the top edge. */
  &::before {
    background: radial-gradient(ellipse at top, rgb(255 255 255 / 0.18), transparent 60%);
    content: '';
    inset: 0;
    pointer-events: none;
    position: absolute;
    z-index: -1;
  }
`;

const Copy = styled.div`
  ${up.lg} {
    padding: 64px 0;
  }
`;

/* Text on the brand gradient is the same in both themes. */
const Eyebrow = styled.p`
  color: color-mix(in oklab, var(--primary-foreground) 85%, transparent);
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.025em;
  line-height: 20px;
  margin: 0;
  text-transform: uppercase;
`;

const Title = styled.h2`
  ${sectionTitleStyle};
  color: var(--primary-foreground);
  margin-top: 12px;
`;

const Body = styled.p`
  color: color-mix(in oklab, var(--primary-foreground) 85%, transparent);
  font-size: 18px;
  line-height: 28px;
  margin: 16px auto 0;
  max-width: 576px;
  text-wrap: pretty;

  ${up.lg} {
    margin-left: 0;
  }
`;

const Actions = styled.div`
  margin-top: 32px;
`;

/* The top of a phone: rounded shoulders, cut off by the panel's bottom edge. */
const Device = styled.div`
  align-self: end;
  background-color: rgb(255 255 255 / 0.25);
  border-radius: 36px 36px 0 0;
  box-shadow: var(--shadow-xl);
  margin: 0 auto;
  max-width: 260px;
  padding: 8px 8px 0;
  width: 100%;

  ${up.lg} {
    margin-top: 48px;
    max-width: 320px;
  }

  img {
    border-radius: 28px 28px 0 0;
    display: block;
    height: auto;
    width: 100%;
  }
`;

/**
 * Call to action for the Bidali mobile app.
 *
 * `source` is appended to the download link's `utm_content`, so each page
 * that shows the banner can be told apart. `background` and `inverted` are
 * accepted for older callers and ignored: the panel is always the brand
 * gradient.
 */
const AppBanner = ({ source }) => {
  const t = useTranslations('appBanner');
  const downloadUrl = source ? `${DOWNLOAD_URL}_${source}` : DOWNLOAD_URL;

  return (
    <Section id="app">
      <Panel>
        <Copy>
          <Eyebrow>{t('eyebrow')}</Eyebrow>
          <Title>{t('title')}</Title>
          <Body>{t('body')}</Body>
          <Actions>
            <Button
              $size="pill"
              $variant="inverse"
              href={downloadUrl}
              rel="noopener noreferrer"
              target="_blank"
            >
              {t('cta')}
            </Button>
          </Actions>
        </Copy>
        <Device>
          <Image
            alt={t('screenshotAlt')}
            height={380}
            src="/images/app-screenshot.avif"
            unoptimized
            width={295}
          />
        </Device>
      </Panel>
    </Section>
  );
};

export default AppBanner;
